/**
 * Typed API errors. Every failure the client can produce is one of these, so a page can
 * show the spec's error state (a plain sentence, a retry and the request id) without
 * guessing what went wrong.
 */

export type ApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'network'
  | 'honesty';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  /** HTTP status, or 0 when the request never got an answer. */
  readonly status: number;
  /** The server's x-request-id, for support. Null when the server did not send one. */
  readonly requestId: string | null;
  /** The parsed error body, when there was one. */
  readonly body: unknown;
  readonly method: string;
  readonly path: string;

  constructor(opts: {
    kind: ApiErrorKind;
    status: number;
    message: string;
    requestId?: string | null;
    body?: unknown;
    method: string;
    path: string;
  }) {
    super(opts.message);
    this.name = new.target.name;
    this.kind = opts.kind;
    this.status = opts.status;
    this.requestId = opts.requestId ?? null;
    this.body = opts.body;
    this.method = opts.method;
    this.path = opts.path;
  }

  /** Worth offering a retry button for. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'server' || this.kind === 'rate_limited';
  }
}

/** 401: no session, an expired one, or a second factor still owed. */
export class UnauthorizedError extends ApiError {}
/** 403: signed in, but this role or scope may not do this. */
export class ForbiddenError extends ApiError {}
/** 404: not found, or outside the caller's scope (the server hides which). */
export class NotFoundError extends ApiError {}
/** 409: a state conflict, for example Scribe with no recording consent. */
export class ConflictError extends ApiError {}
/** 400 / 422: the request was malformed. */
export class ValidationError extends ApiError {}
/** 429. */
export class RateLimitedError extends ApiError {}
/** 5xx. */
export class ServerError extends ApiError {}
/** The request never reached the server, or the answer was not readable. */
export class NetworkError extends ApiError {}
/**
 * The server answered 2xx but without the honesty flag (`real`) the route promises.
 * The UI must never show such a figure as fact, so the client refuses it.
 */
export class HonestyError extends ApiError {}

export function errorForStatus(
  status: number,
  base: { message: string; requestId: string | null; body: unknown; method: string; path: string },
): ApiError {
  const o = { ...base, status };
  if (status === 401) return new UnauthorizedError({ ...o, kind: 'unauthorized' });
  if (status === 403) return new ForbiddenError({ ...o, kind: 'forbidden' });
  if (status === 404) return new NotFoundError({ ...o, kind: 'not_found' });
  if (status === 409) return new ConflictError({ ...o, kind: 'conflict' });
  if (status === 429) return new RateLimitedError({ ...o, kind: 'rate_limited' });
  if (status === 400 || status === 422) return new ValidationError({ ...o, kind: 'validation' });
  return new ServerError({ ...o, kind: 'server' });
}
